import { isBefore, addDays, parseISO, isValid } from 'date-fns';
import { fg } from '@atlassian/jira-feature-gating';
import { logSafeErrorWithoutCustomerDataWrapper } from '@atlassian/jira-polaris-lib-errors/src/common/utils/index.tsx';

import type { IntervalValue } from './types.tsx';

export const RELATIVE = 'RELATIVE' as const;
export const ABSOLUTE = 'ABSOLUTE' as const;

type TimestampDisplaySetting = typeof RELATIVE | typeof ABSOLUTE;

const getDaysElapsed = (date: string | number) =>
	(Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24);

// In accordance with the ADG spec for date formats (https://hello.atlassian.net/wiki/spaces/ADG/pages/195123084/Date+formats+product+1.0+spec)
export const getADGTimestampDisplaySetting = (date: string | number): TimestampDisplaySetting =>
	getDaysElapsed(date) < 8 ? RELATIVE : ABSOLUTE;

const MINUTE = 60;
const HOUR = 60 * 60;
const DAY = 60 * 60 * 24;

type RelativeTimeFormatSingularUnit = 'second' | 'minute' | 'hour' | 'day';

// stolen from react-intl
function getDurationInSeconds(unit?: RelativeTimeFormatSingularUnit): number {
	switch (unit) {
		case 'second':
			return 1;
		case 'minute':
			return MINUTE;
		case 'hour':
			return HOUR;
		default:
			return DAY;
	}
}

const selectUnit = (seconds: number): RelativeTimeFormatSingularUnit => {
	const absValue = Math.abs(seconds);

	if (absValue < MINUTE) {
		return 'second';
	}

	if (absValue < HOUR) {
		return 'minute';
	}

	if (absValue < DAY) {
		return 'hour';
	}

	return 'day';
};

export const getRelativeValueAndUnit = (
	date: string | number,
): [number, RelativeTimeFormatSingularUnit] => {
	const seconds = Math.round((new Date(date).getTime() - Date.now()) / 1000);
	const unit = selectUnit(seconds);
	const unitDuration = getDurationInSeconds(unit);
	const value = Math.round(seconds / unitDuration);
	return [value, unit];
};

export const isProjectOnboardedWithinAWeek = (onboardedAt: string | null | undefined) => {
	if (!onboardedAt) {
		return false;
	}

	const now = new Date();
	const then = new Date(onboardedAt);
	return isBefore(now, addDays(then, 7));
};

const isValidIsoDateString = (value: string | undefined) => value && isValid(parseISO(value));

export const tryParseIntervalString = (value: string | undefined): IntervalValue | undefined => {
	if (fg('jpd-safe-interval-parse')) {
		return tryParseIntervalStringNew(value);
	}

	if (!value) {
		return undefined;
	}

	return tryParseIntervalStringOld(value);
};

const tryParseIntervalStringOld = (value: string): IntervalValue | undefined => {
	if (isValidIsoDateString(value)) {
		return {
			start: value,
			end: value,
		};
	}
	try {
		const interval = JSON.parse(value);
		return isValidIsoDateString(interval.start) && isValidIsoDateString(interval.end)
			? interval
			: undefined;
	} catch (error) {
		return undefined;
	}
};

const tryParseIntervalStringNew = (value: string | undefined): IntervalValue | undefined => {
	if (!value) {
		return undefined;
	}

	if (isValidIsoDateString(value)) {
		return {
			start: value,
			end: value,
		};
	}

	try {
		const parsedValue = JSON.parse(value);

		if (!isValidIsoDateString(parsedValue.start) || !isValidIsoDateString(parsedValue.end)) {
			return undefined;
		}

		return {
			start: parsedValue.start,
			end: parsedValue.end,
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		logSafeErrorWithoutCustomerDataWrapper(
			'polaris-error.lib-date-time.tryParseIntervalString',
			'Failed to parse Interval JSON',
			error,
		);
		return undefined;
	}
};
